import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Mail, Zap, Globe, CheckCircle2, Cpu, TrendingUp } from 'lucide-react';

export default function WomenInAI() {
  // Animation settings
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    whileInView: { transition: { staggerChildren: 0.2 } }
  };

  return (
    <section className="py-5 bg-white" style={{ overflow: 'hidden' }}>
      <div className="container">
        
        {/* --- SECTION HEADER --- */}
        <motion.div 
          className="text-center mb-5"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <h6 className="text-darkpurple fw-bold text-uppercase mb-2" style={{ letterSpacing: '2px' }}>
            Success Story
          </h6>
          <h2 className="display-4 fw-bold mb-3">Elevating Global Analytics for Women in AI</h2>
          <div className="mx-auto bg-darkpurple mb-4" style={{ height: '4px', width: '60px', borderRadius: '2px' }}></div>
          <p className="lead text-secondary mx-auto" style={{ maxWidth: '800px' }}>
            LuranTech engineered a sophisticated, automated data pipeline to unify engagement insights across 
            <strong> 40+ global chapters</strong> for the world's leading community in Artificial Intelligence.
          </p>
        </motion.div>

        <div className="row g-4 align-items-stretch">
          
          {/* --- CHALLENGE CARD --- */}
          <motion.div className="col-lg-6" {...fadeInUp}>
            <div className="card h-100 border-0 shadow-lg p-4 p-md-5" style={{ borderRadius: '20px', background: '#f8f9fa' }}>
              <div className="d-flex align-items-center mb-4">
                <div className="bg-white shadow-sm p-3 rounded-circle me-3">
                  <BarChart3 className="text-darkpurple" size={28} />
                </div>
                <h3 className="h4 fw-bold mb-0">The Challenge</h3>
              </div>
              <p className="text-muted mb-4">
                As a rapidly growing global organization, WAI faced complexities in maintaining 
                centralized visibility over their diverse digital footprint.
              </p>
              <ul className="list-unstyled">
                {[
                  'Fragmented data across 40+ independent regional pages',
                  'High operational overhead in weekly report consolidation',
                  'Inconsistent reporting formats across different global timezones'
                ].map((item, i) => (
                  <li key={i} className="mb-3 d-flex align-items-start">
                    <span className="badge rounded-circle bg-danger bg-opacity-10 text-danger me-2 mt-1 px-2 pb-1">!</span>
                    <span className="text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* --- SOLUTION CARD --- */}
          <motion.div className="col-lg-6" {...fadeInUp} transition={{ delay: 0.2 }}>
            <div className="card h-100 border-0 shadow-lg p-4 p-md-5 text-white" 
                 style={{ borderRadius: '20px', background: 'linear-gradient(279.39deg, #5060E5 -11.76%, #7F60A6 35.07%, #352482 80.72%)' }}>
              <div className="d-flex align-items-center mb-4">
                <div className="bg-white bg-opacity-20 p-3 rounded-circle me-3">
                  <Cpu className="text-darkpurple" size={28} />
                </div>
                <h3 className="h4 fw-bold mb-0">The LuranTech Solution</h3>
              </div>
              <p className="text-white text-opacity-75 mb-4">
                We deployed an enterprise-grade automation engine designed for high-precision data harvesting and delivery.
              </p>
              <ul className="list-unstyled">
                {[
                  'Custom LinkedIn API integration for real-time metric synchronization',
                  'Intelligent data aggregation and standardization layer',
                  'Autonomous cloud-based delivery system for stakeholders'
                ].map((item, i) => (
                  <li key={i} className="mb-3 d-flex align-items-start">
                    <CheckCircle2 className="me-2 text-white mt-1" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* --- RESULTS GRID --- */}
        <motion.div 
          className="row g-4 mt-5"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          {[
            { 
              icon: <Zap className="text-darkpurple" size={32} />, 
              title: '100% Autonomous', 
              desc: 'Completely removed operational bottlenecks.' 
            },
            { 
              icon: <Globe className="text-darkpurple" size={32} />, 
              title: '40+ Chapters', 
              desc: 'Unified global visibility in a single report.' 
            },
            { 
              icon: <TrendingUp className="text-darkpurple" size={32} />, 
              title: 'Data-Driven', 
              desc: 'Accurate insights for strategic decision making.' 
            },
            { 
              icon: <Mail className="text-darkpurple" size={32} />, 
              title: 'Weekly Sync', 
              desc: 'Automated delivery directly to leadership inbox.' 
            }
          ].map((stat, index) => (
            <motion.div key={index} className="col-6 col-md-3" variants={fadeInUp}>
              <div className="text-center p-4 border-0 h-100 transition-card">
                <div className="text-primary mb-3 d-flex justify-content-center">{stat.icon}</div>
                <h5 className="fw-bold mb-2">{stat.title}</h5>
                <p className="text-muted small mb-0">{stat.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .transition-card {
          transition: all 0.3s ease;
          border-radius: 15px;
        }
        .transition-card:hover {
          background: #ffffff;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          transform: translateY(-5px);
        }
      `}} />
    </section>
  );
}